/* eslint-disable @typescript-eslint/no-namespace */
import { FormatDate } from "../_objects/format-date";
import { TENANT_ROLE, TenantConfig, UserConfig } from "../app.constants";
import { IIndividualReportTemplateSchema } from "./models/individual-report-template.schema";
import { ILoggedInUserSchema } from "./models/logged-in-user.schema";
import { IMarkSchema } from "./models/mark.schema";
import { SubjectAttributesSchema } from "./models/subject-attributes.schema";
import { ISubjectSchema } from "./models/subject.schema";
import { ISubjectModelLocationsSchema } from "./models/subjectModel-locations.schema";
import { ISubjectModelSchema } from "./models/subjectModel.schema";
import { ITenantDetailSchema } from "./models/tenant-detail.schema";
import { ITenantConfigSchema } from "./models/tenantConfiguration.schema";
import { ITitleSchema } from "./models/title.schema";
import { IUserProfileSchema } from "./models/user-profile.schema";
import { IUserSchema } from "./models/user.schema";
import { IUserConfigSchema } from "./models/userConfiguration.schema";
import { REPORT_TEMPLATE_4x2_LAYOUT } from "./templates/report-template-4x2-layout";
import { REPORT_TEMPLATE_4x3_LAYOUT } from "./templates/report-template-4x3-layout";
import { AVIATION_ENGINE_LOCATIONS } from "./standalone-data/aviation_engine_locations";
import { INDUSTRIAL_GAS_TURBINE_LOCATIONS } from "./standalone-data/industrial_gas_turbine_locations";
import { WIND_TURBINE_GEARBOX_LOCATIONS } from "./standalone-data/wind_turbine_gearbox_locations";

export const IMAGE_CONVERTER_VERSION = "0.0.7";
export const HTMLTOPDF_CONVERTER_VERSION = "0.0.7";
export const EXIF_METADATA_READER_VERSION = "0.0.6";
export const STEREO_MEASUREMENT_VEGA_VERSION = "0.0.11";
export const STEREO_MEASUREMENT_IV7_VERSION = "0.0.10";
export const STEREO_MEASUREMENT_3D_VERSION = "0.0.22";
/**
 * Version of the Merry measurement service bundled with the desktop app (GitLab generic package
 * `measurement_service_dist/main/measurement_service_dist_<version>.zip`; its `dist/bin` and
 * `dist/config` folders are shipped under `assets/offline/measurement`). The service must stay
 * wire-compatible with the gRPC client embedded in `@evident/ngx-media-viewer`: versions from
 * 2606.126.0.229170 onwards (MERRY-2093) add a `Result` field at position 1 of every response and
 * need media viewer 21 or newer.
 */
export const MEASUREMENT_SERVICE_VERSION = "2610.116.0.274712";
/**
 * Default gRPC port of the measurement service: used by the local scripts (`merry:start:measurement-service`, backend
 * of the grpcwebproxy) and as fallback by the desktop app. The desktop app normally passes a free 127.0.0.1 port picked
 * at startup as `--grpc_port`, because the Merry application bundles the same service with the same default port and
 * both run side by side on an IPLEX One device.
 */
export const MEASUREMENT_SERVICE_GRPC_PORT = 50053;
/**
 * Named pipe of the frontend IPC server of the service, on which it delivers reconstruction meshes. Passed as
 * `--ipc_server_frontend_pipe_name`, overriding `ipc_server_frontend.pipe_name` of `config/measurement_config.json`,
 * which is shipped unchanged. It must differ from Merry's default "measurement_frontend": a named pipe server can only
 * be created by one process, and the service started second blocks forever in "IpcServerWindows frontend pipe is busy,
 * retrying..." without ever starting its gRPC server (whichever of ViSOL or Merry starts second would lose).
 */
export const MEASUREMENT_SERVICE_FRONTEND_PIPE = "visol_measurement_frontend";

export namespace IPC_CHANNEL {
  export namespace MAIN_PROCESS {
    export const MAIN_WINDOW_CLOSE = "main-window-close";
  }

  export namespace ZOOM_CONTROL {
    export const SET_ZOOM = "set-zoom";
    export const ZOOM_CHANGED = "zoom-changed";
  }

  export namespace UTILS {
    export const CONVERT_TO_JPEG = "convert-to-jpeg";
    export const CONVERT_TO_MP4 = "convert-to-mp4";
    export const EXTRACT_VIDEO_THUMBNAIL = "extract-video-thumbnail";
    export const EXIT_APP = "exit-app";
    export const GENERATE_THUMBNAIL_TO_HTML = "generate-thumbnail-to-html";
    export const GET_APP_DIRECTORY = "get-app-directory";
    export const GET_LOCAL_FOLDER_PATH = "get-local-folder-path";
    export const IPLEX_ONE = "iplex-one";
    export const IPLEX_ONE_END = "iplex-one-end";
    export const LOAD_APP = "load-app";
    export const PRINT_TO_PDF = "print-to-pdf";
    export const READ_EXIF_INFO = "read-exif-info";
    export const WRITE_LOG = "write-log";
    export const EXECUTE_BAT_FILE = "execute-bat-file";

    // Check internet connectivity
    export const CHECK_INTERNET_CONNECTION = "check-internet-connection";

    // Exec CMD/PowerShell command
    export const EXEC_POWERSHELL_COMMAND = "exec-powershell-command";
  }

  export namespace DB {
    export const DELETE_ALL = "delete-all-db";
  }

  export namespace COOKIE {
    export const GET_ALL = "get-cookies";
    export const SET = "set-cookie";
    export const DELETE_ALL = "delete-all-cookies";
    export const GET_ALL_REPLY = "get-all-cookies-reply";
  }

  export namespace FILE_MANAGER {
    export const GET_BRIAN_FOLDERS = "get-brian-folders";
    // File system acces
    export const LIST_DRIVES = "list-drives";
    export const LIST_FILES_AND_FOLDERS = "list-files-and-folders";
    export const SCAN_FOLDER_FILES = "scan-folder-files";
    export const FILES_EXIST = "files-exist";
    export const READ_FILES = "read-files";
    export const WRITE_FILES = "write-files";
  }

  export namespace FILE {
    export const GET_APP_FOLDER_PATH = "get-app-folder-path";
    export const GET_USER_PATH = "get-user-path";
    export const GET_SYSTEM_PATH_DELIMITER = "get-system-path-delimiter";
    export const BROWSE_SAVE_PATH = "browse-save-path";
    export const GET_FILE = "get-file";
    export const READ_TEXT_FILE = "read-text-file";
    export const UPDATE_IMAGE_PREFIX_FILE = "update-image-prefix-file";
    export const POST_TEXT_FILE = "post-text-file";
    export const PUT_ONE = "put-file";
    export const PUT_FILE_PATH = "put-file-path";
    export const DELETE_ONE = "delete-file";
    export const DELETE_FILE_FORCE = "delete-file-force";
    export const DELETE_FOLDER = "delete-folder";
    export const CREATE_FOLDER = "create-folder";
    export const OPEN_EXPLORER = "open-explorer";
    export const IS_FILE_EXISTS = "is-file-exists";
    export const SAVE_FILE = "save-file";
    export const READ_FILES = "read-files";
    export const CHECK_FILE_ACCESS = "check-file-access";
  }

  export namespace BRIAN {
    export const UNZIP_TO_BRIAN_INSPECTIONS_FOLDER = "unzip-to-brian-inspections-folder";
    export const GET_ALL_INSPECTIONS_FOLDERS = "get-folders";
    export const READ_LOCK_IMAGE_FILE = "read-lock-image-file";
    export const READ_IMAGE_PREFIX = "read-image-prefix";
    export const UPDATE_IMAGE_LOCK_FILE = "update-image-lock-file";
    export const UPDATE_IMAGE_PREFIX = "update-image-prefix";
  }

  export namespace APP_INFO {
    export const ID = "electron_app_info";
    export const GET = "get-app-info";
    export const PUT = "put-app-info";
    export const PUT_NETWORK_MODE = "put-network-mode";
    export const PUT_CLIENT_NAME = "put-client-name";
    export const AFTER_START_UP = "get-after-start-up";
    export const PUT_SWITCH_BY = "put-app-info-switch-by";
    export const PUT_REGION = "put-app-info-environment";
    export const PUT_APP_LAUNCH_TYPE = "put-app-launch-type";
    export const HOLD_REGION = "hold-region";
    export const HOLD_APP_LAUNCH_TYPE = "hold-app-launch-type";
  }

  export namespace APP_VERSION {
    export const GET_ALL = "get-app-version-list";
  }

  export namespace UPLOAD_MANAGE {
    export const GET_ALL = "get-upload-manage-all";
    export const GET = "get-upload-manage";
    export const PUT_ALL = "put-upload-manage-all";
    export const PUT = "put-upload-manage";
    export const DELETE = "delete-upload-manage";
    export const DELETE_LIST = "delete-upload-manage-list";
    export const DELETE_BY_PARENT_ID = "delete-upload-manage-by-parentId";
    export const DELETE_RELATED_ID = "delete-upload-manage-related-id";
    export const UPLOADED = "uploaded-upload-manage";
  }

  export namespace CURRENT_USER {
    export const ID = "electron_current_user";
    export const GET = "get-current-user";
    export const PUT = "put-current-user";
  }

  export namespace LOGGED_IN_USER {
    export const GET_ALL = "get-logged-in-user-all";
    export const GET = "get-logged-in-user";
    export const GET_CURRENT_USER = "get-current-logged-in-user";
    export const PUT = "put-logged-in-user";
    export const PUT_IS_AGREED_TERMS = "put-is-agreed-terms";
    export const DELETE = "delete-logged-in-user";
  }

  export namespace MARK {
    export const GET_ALL = "get-marks-all";
    export const PUT_ALL = "put-marks-all";
  }

  export namespace SUBJECT {
    export const GET_ALL = "get-subjects-all";
    export const GET_ALL_SUBJECTS_VERSION = "get-subjects-all-versions";
    export const PUT_ALL = "put-subjects-all";
    export const GET_ONE = "get-subject";
    export const PUT_ONE = "put-subject";
    export const IS_SAME_SERIAL_NUMBER_EXISTS = "is-same-serial-number-exists";
    export const UPDATE_SUBJECT_ATTRIBUTES_NEWEST_VERSION = "update-subject-attributes-newest-version";
  }

  export namespace LANGUAGE {
    export const GET_OS_LANGUAGE = "get-os-language";
  }

  export namespace SUBJECT_ATTRIBUTES {
    export const GET_ALL = "get-subjects-attributes-all";
    export const PUT_ALL = "put-subjects-attributes-all";
    export const GET_ONE = "get-subject-attributes";
  }

  export namespace FILE_WATCHER {
    export const MONITOR = "monitor-brian-folder";
    export const DELETE_FILE = "delete-brian-file";
    export const UNMONITOR = "stop-brian-folder";
  }

  export namespace SUBJECT_MODEL {
    export const GET_ALL = "get-subjectModels-all";
    export const PUT_ALL = "put-subjectModels-all";
    export const GET_ONE = "get-subjectModel";
    export const PUT_ONE = "put-subjectModel";
    export const IS_SAME_SUBJECT_MODEL_NAME_EXISTS = "is-same-subject-model-name-exists";
    export const PUT_IMPORTED_SUBJECT_MODEL = "put-imported-subject-model";
    export const PUT_IMPORTED_SUBJECT_MODEL_IMAGE = "put-imported-subject-model-image";
    export const DELETE = "delete-subjectModel";
    export const HAS_INSPECTIONS = "has-inspections";
  }

  export namespace SUBJECT_MODEL_LOCATIONS {
    export const GET_ALL = "get-subjectModels-locations-all";
    export const PUT_ALL = "put-subjectModels-locations-all";
    export const GET_ONE = "get-subjectModel-locations";
    export const GET_ONE_BY_INSPECTION_ID = "get-subjectModel-locations-by-inspection-id";
  }

  export namespace USER {
    export const GET_ALL = "get-user-all";
    export const PUT_ALL = "put-user-all";
    export const GET_ONE = "get-user";
  }

  export namespace USER_PROFILE {
    export const GET_ONE = "get-user-profile";
    export const PUT_ONE = "put-user-profile";
  }

  export namespace TITLE {
    export const GET_ALL = "get-title-all";
    export const PUT_ALL = "put-title-all";
  }

  export namespace TAGS {
    export const GET_ALL = "get-tags-all";
    export const PUT_ALL = "put-tags-all";
    export const PATCH_ONE = "patch-tags-one";
  }

  export namespace TENANT_CONFIG {
    export const GET_ALL = "get-tenant-config-all";
    export const PUT_ALL = "put-tenant-config-all";
  }

  export namespace USER_CONFIG {
    export const GET_ALL = "get-user-config-all";
    export const PUT_ALL = "put-user-config-all";
  }

  export namespace TENANT_DETAIL {
    export const GET_ONE = "get-tenant-detail";
    export const PUT_ONE = "put-tenant-detail";
  }

  export namespace INSPECTION {
    export const GET_LIST_ALL = "get-inspection-list-all";
    export const GET_LIST_BY_ID = "get-inspection-list-by-id";
    export const PUT_LIST_ALL = "put-inspection-list-all";
    export const GET_ONE = "get-inspection";
    export const GET_WITH_SUBJECT = "get-inspection-with-subject";
    export const PUT_ONE = "put-inspection";
    export const DELETE_ONE = "delete-inspection";
    export const IS_INSPECTION_USING_SUBJECTID_EXISTS = "is-inspection-using-subjectid-exists";
    export const GET_COUNT = "get-inspection-count";
  }

  export namespace CONNECTOR {
    export const GET_STATUS = "get-connector-status";
    export const UPDATE_STATUS = "update-connector-status";
  }

  export namespace INSPECTION_IMAGE {
    export const GET_ALL = "get-inspection-image-all";
    export const GET_LIST = "get-inspection-image-list";
    export const GET_LIST_BY_ID = "get-inspection-image-list-by-id";
    export const GET_ONE = "get-inspection-image";
    export const GET_ONE_BY_FILEPATH = "get-inspection-image-by-filepath";
    export const GET_ONE_BY_FILENAME_AND_LOCATION = "get-inspection-image-by-fileName-and-location";
    export const GET_ONE_BY_FILENAME_AND_LOCATION_WITHOUT_EXTENSION = "get-inspection-image-by-fileName-and-location-without-extension";
    export const GET_TMP_FILE = "get-inspection-image-tmp-file";

    export const PUT_LIST = "put-inspection-image-list";
    export const PUT_ONE = "put-inspection-image";
    export const PUT_MODIFIED_USE = "put-inspection-image-modified-use";

    export const DELETE_ALL = "delete-inspection-image-all";
    export const DELETE_FOR_UPLOAD = "delete-inspection-image-for-upload";
    export const DELETE_LIST = "delete-inspection-image-list";
    export const DELETE_ONE = "delete-inspection-image";
  }

  export namespace INDIVIDUAL_REPORT {
    export const GET_LIST = "get-individual-report-list";
    export const GET_ONE = "get-individual-report";
    export const GET_ALL_VERSION = "get-individual-report-all-version";
    export const GET_BY_INSPECTION_IDS = "get-individual-report-by-inspection-ids";
    export const GET_BY_IDS = "get-individual-report-by-ids";
    export const PUT_ONE = "put-individual-report";
    export const PUT_THUMB_PATH = "put-individual-report-thumb-path";
    export const PUT_ALL = "put-individual-report-All";
    export const DELETE_ONE = "delete-individual-report";
    export const DELETE_ALL = "delete-individual-report-all";
    export const IS_INDIVIDUAL_REPORT_USING_TEMPLATEID_EXISTS = "is-individual-report-using-templateid-exists";
    export const GET_COUNT = "get-individual-report-count";
  }

  export namespace INDIVIDUAL_REPORT_TEMPLATE {
    export const PUT_ALL = "put-individual-report-template-all";
    export const PUT_ONE = "put-individual-report-template";
    export const GET_ALL = "get-individual-report-template-all";
    export const GET_ONE = "get-individual-report-template";
    export const HAS_REPORTS = "individual-report-template-has-reports";
    export const DELETE = "delete-individual-report-template";
  }

  export namespace HTTPS_TO_IPLEX {
    export const HTTPS = "https-iplex-device-request";
  }

  export namespace WEB_DAV {
    export const GET_CONTENTS = "get-contents-for-webDAV";
    export const GET_FILE_BASE64 = "get-file_base64-for-webDAV";
    export const SAVE_FILE = "get-save-for-webDAV";
    export const PUT_DIRECTORY = "put-directory-for-webDAV";
    export const PUT_FILE = "put-file-for-webDAV";
    export const DELETE_FILE = "delete-file-for-webDAV";
    export const DELETE_FOLDER = "delete-folder-for-webDAV";

    export const SAVE_STREAM = "get-save-stream-for-webDAV";
    export const STREAM_SIZE = "get-save-for-webDAV:STREAM_SIZE";
    export const STREAM_ERROR = "get-save-for-webDAV:STREAM_Error";

    export const SAVE_CANCEL = "get-save-cancel-for-webDAV";
  }

  export namespace MEASUREMENT {
    export const GET3DMODELIMAGEDATAFILE = "measurement-get-3d-model-image-data-file";
    export const GETLIBRARYVERSION = "measurement-get-library-version";
    export const GETPOINTSET = "measurement-get-point-set";
    export const GETPOLYLINE = "measurement-get-poly-line";
    export const GETRECONSTRUCTION = "measurement-get-reconstruction";
    export const ISEXISTSINTERNALDATA = "measurement-is-exists-internal-data";
    export const MEASUREAREA = "measurement-measure-area";
    export const MEASUREDEPTH = "measurement-measure-depth";
    export const MEASUREDISTANCE = "measurement-measure-distance";
    export const MEASURELINES = "measurement-measure-lines";
    export const MEASUREPOINTTOLINE = "measurement-measure-point-to-line";
    export const MEASUREMISSINGCORNER = "measurement-measure-missing-corner";
    export const RECONSTRUCT = "measurement-reconstruct";
  }

  export namespace CONNECT_DEVICE {
    export const GET_ALL = "get-connect-device-all";
    export const GET = "get-connect-device";
    export const PUT = "put-connect-device";
  }

  export namespace MEASUREMENTINTERMEDIATEINFO {
    export const GET_ONE = "get-measurement-intermediate-info";
    export const PUT_ONE = "put-measurement-intermediate-info";
    export const DELETE_ONE = "delete-measurement-intermediate-info";
    export const DELETE_MULTIPLE = "delete-measurement-intermediate-info-multiple";
  }

  export namespace MIGRATION_STATE {
    export const GET = "get-migration-state";
    export const PUT = "put-migration-state";
  }

  export namespace ASSET_MODULE_BUNDLE {
    export const ID = "asset_module_bundle";
    export const GET = "get-asset-module-bundle";
    export const PUT = "put-asset-module-bundle";
    export const DELETE = "delete-asset-module-bundle";
  }
}

export namespace OFFLINE_REGEX {
  // ISO8859-1 の英数字、記号キーボード(0x20 ～ 0x7E) ファイル名/フォルダー名に使用できない記号(|\?<>/:"*)を除く
  export const File_Stat_Basename = /^([\x21]|[\x23-\x29]|[\x2b-\x2e]|[\x30-\x39]|[\x3b]|[\x3d]|[\x40-\x5b]|[\x5d-\x7b]|[\x7d-\x7e])*$/;
}

export namespace FILE_SYSTEM {
  export namespace FILE_NAME {
    export const RETURN_APP_BAT_FILE = "ReturnAPP.bat";
  }

  export namespace PATH {
    export const SYSTEM_DRIVE = "C:";
    export const FILE_PREFIX = "file:///";
    export const BLOBS = "Blobs";
    export const TEMP = "Temp";
    export const REPORT_TEMPLATE = "reportTemplates";
    export const SUBJECT_MODEL = "subjectModels";
    export const IPLEX_ONE_RETURN_APP = "IPLEXOne";
    export const IPLEX_ONE = "IPLEX\\One";
    export const PRESET = "PRESET";
    export const CONFIG = "Config";
    export const MONITOR = "Monitor";
    export const LOGO = "LOGO";
    export const COMPARE = "Compare";
    export const LANGUAGE = "Language";
  }
}

export namespace DATA_SYNC_STEP_COUNT {
  export const OFFLINE_DATA_SYNC = 4;
  export const ONLINE_DATA_SYNC = 1;
  export const CHECKOUT_DATA_SYNC = 2;
}

/**
 * - 検査情報の同期（上り）
 * - 検査画像情報の同期（上り）
 * - 検査画像の同期（上り）
 * - レポート情報の同期（上り）
 * - individual reportの画像データ同期（下り）
 * - オフライン設定データ同期（下り）
 * - subject modelの画像同期（下り）
 * - オフラインの画像設定データを同期（下り）
 * - ロゴ画像同期（下り）
 */
export enum DATA_SYNC_STEP {
  UPLOAD_INSPECTION,
  UPLOAD_INSPECTION_IMAGE_INFO,
  UPLOAD_INSPECTION_IMAGE_FILE,
  UPLOAD_REPORT_DATA,
  DOWNLOAD_REPORT_TEMPLATE_IMAGE_FILE,
  DOWNLOAD_OFFLINE_SETTINGS,
  DOWNLOAD_SUBJECT_MODEL_IMAGE_FILE,
  DOWNLOAD_IMAGE_FILE,
  DOWNLOAD_LOGO_IMAGE_FILE,
  DOWNLOAD_CHECKED_OUT_INSPECTIONS,
  DOWNLOAD_ASSET_MODULE_DATA
}

export namespace REGION_CONFIG {
  export const UNITED_STATES = "unitedStates";

  export class RegionInfo {
    country: string;
    langType: string;

    constructor(country: string, langType: string) {
      this.country = country;
      this.langType = langType;
    }
  }

  export const VALUES: RegionInfo[] = [new RegionInfo(UNITED_STATES, "en")];
}

export namespace OFFLINE_REPORT_PREVIEW {
  export enum FormatMode {
    Display,
    JsonData
  }
}

/**
 * HttpErrorResponse で使用するエラーメッセージ。
 */
export namespace HttpErrorMessage {
  /** HttpStatusCode.BadRequest で使用するエラーメッセージ。 */
  export namespace BadRequest {
    export const DataImport: string = "This data cannot be imported. Reason: ";
  }

  /** HttpStatusCode.Conflict で使用するエラーメッセージ。 */
  export namespace Conflict {
    export const ReportTemplateAlreadyUsed: string = "This report template is already used by the inspection report. Id: ";
    export const SubjectAlreadyExists: string = "This subject already exists. SerialNo: ";
    export const SubjectModelAlreadyExists: string = "This subject model already exists. ModelName: ";
    export const SubjectModelLocationIdentifierDuplicated: string = "This subject model identifier is is duplicated.";
  }

  /** HttpStatusCode.NotFound で使用するエラーメッセージ。 */
  export const DataNotFound: string = "This data is not found.";

  /** HttpStatusCode.InternalServerError で使用するエラーメッセージ。 */
  export namespace InternalServerError {
    export const SQLError: string = "SQL error.";
  }
}

/**
 * HttpErrorResponse で使用する StatusText。
 */
export namespace HttpStatusText {
  /** HttpStatusCode.BadRequest で使用する StatusText。*/
  export const BadRequest: string = "BadRequest";
  /** HttpStatusCode.Conflict で使用する StatusText。*/
  export const Conflict: string = "Conflict";
  /** HttpStatusCode.InternalServerError で使用する StatusText。*/
  export const InternalServerError: string = "Internal Server Error";
  /** HttpStatusCode.NotFound で使用する StatusText。*/
  export const NotFound: string = "Not Found";
}

/**
 * App 起動タイプ。
 */
export namespace APP_LAUNCH_TYPE {
  /** ViSOL Model (旧Basic) */
  export const ViSOL = "Basic";
  /** IPLEX Mode (旧Standalone) */
  export const IPLEX = "Standalone";

  /** 起動タイプの一覧。 */
  export const VALUES: string[] = [ViSOL, IPLEX];
}

export enum LOG_TYPE {
  INFO = "info",
  ERROR = "error"
}

export enum DataProperty {
  WorstOnly = 0,
  All = 1
}

/**
 * MEASUREMENT 用定数。
 */
export namespace MEASUREMENT {
  /** InternalData のファイル名。 */
  export const INTERNAL_DATA_FILE: string = "internalData";
  /** PointCloud のファイル名。 */
  export const POINT_CLOUD_FILE: string = "pointCloud";
  /** Measurement 画像ディレクトリー名。 */
  export const DIRECTORY_NAME: string = "measurement";
  /** Measurement 画像ファイルの Prefix。 */
  export const FILE_PREFIX: string = DIRECTORY_NAME + "_";
  /** Reconstruct 結果ファイル名。 */
  export const RECONSTRUCTION_FILE: string = "reconstruct.json";
}
